import { useMemo, useState } from 'react';
import { BRAND_LABEL, COUNTRY_CODES, IBAN_LENGTHS, parsePayoutDetails, PAYOUT_RAIL, type PayoutAccount, type PayoutCurrency, type PayoutKind } from '@platform/shared';
import { cx } from '../../../components/ui';
import { api, ApiError } from '../../../lib/api';
import { IP_COUNTRY } from '../../../lib/config';
import { errMsg, Field, PillButton } from '../kit';

const countries = (() => {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    /* old browser */
  }
  return COUNTRY_CODES.map((c) => ({ code: c, name: dn?.of(c) ?? c })).sort((a, b) => a.name.localeCompare(b.name));
})();
const countryName = (cc: string) => countries.find((c) => c.code === cc)?.name ?? cc;

interface Spec {
  key: string;
  label: string;
  placeholder?: string;
  hint?: string;
  wide?: boolean;
  mono?: boolean;
  optional?: boolean;
  numeric?: boolean;
  select?: { value: string; label: string }[];
}

const HOLDER: Spec = { key: 'holder', label: 'Account holder name', placeholder: 'As on your ID', hint: 'Must match your verified name. We cannot pay a third party.', wide: true };

/** The bank fields asked for each payout currency, and the card fields. */
const SPECS: Record<PayoutCurrency, Spec[]> & { card: Spec[] } = {
  GBP: [
    HOLDER,
    { key: 'sortCode', label: 'Sort code', placeholder: '12-34-56', hint: '6 digits', mono: true, numeric: true },
    { key: 'accountNumber', label: 'Account number', placeholder: '12345678', hint: '8 digits', mono: true, numeric: true },
    { key: 'bankName', label: 'Bank name', placeholder: 'e.g. Barclays' },
  ],
  EUR: [
    HOLDER,
    { key: 'iban', label: 'IBAN', placeholder: 'DE89 3704 0044 0532 0130 00', wide: true, mono: true },
    { key: 'bic', label: 'BIC / SWIFT', placeholder: 'COBADEFFXXX', hint: '8 or 11 characters', mono: true },
    { key: 'bankName', label: 'Bank name', placeholder: 'e.g. Commerzbank' },
  ],
  USD: [
    HOLDER,
    { key: 'holderAddress', label: 'Account holder address', placeholder: 'Street, city, postcode, country', hint: 'Required on SWIFT transfers', wide: true },
    { key: 'swift', label: 'SWIFT / BIC', placeholder: 'e.g. HBMEAEAD', hint: '8 or 11 characters', mono: true },
    { key: 'account', label: 'Account number or IBAN', placeholder: 'USD account number', hint: 'Must be a USD account', mono: true },
    { key: 'bankName', label: 'Bank name', placeholder: 'e.g. HSBC' },
    { key: 'bankCountry', label: 'Bank country', select: [{ value: '', label: 'Choose a country' }, ...countries.map((c) => ({ value: c.code, label: c.name }))] },
    { key: 'bankAddress', label: 'Bank address', placeholder: 'Branch street, city, country', wide: true },
    { key: 'aba', label: 'ABA routing number', placeholder: '9 digits', hint: 'Only for banks in the United States', mono: true, optional: true, numeric: true },
    { key: 'intermediarySwift', label: 'Intermediary bank SWIFT', placeholder: 'e.g. CITIUS33', hint: 'Only if your bank gave you one', mono: true, optional: true },
  ],
  card: [
    { ...HOLDER, label: 'Cardholder name', placeholder: 'As printed on the card' },
    {
      key: 'brand',
      label: 'Card brand',
      select: [
        { value: '', label: 'Choose' },
        ...Object.entries(BRAND_LABEL).map(([value, label]) => ({ value, label })),
      ],
    },
    { key: 'last4', label: 'Last 4 digits', placeholder: '4242', hint: 'Never the full card number', mono: true, numeric: true },
    { key: 'expiry', label: 'Expiry', placeholder: 'MM/YY', mono: true, numeric: true },
    { key: 'issuer', label: 'Issuing bank', placeholder: 'e.g. Revolut' },
  ],
};

const NOTE: Record<PayoutCurrency, string> & { card: string } = {
  GBP: 'For UK bank accounts. We pay by Faster Payments, or CHAPS for large amounts.',
  EUR: 'For EUR accounts in the SEPA zone. We pay by SEPA credit transfer to your IBAN.',
  USD: 'For USD accounts anywhere. We pay by SWIFT wire. Your bank or an intermediary bank may deduct its own charges.',
  card: 'Use a card you have deposited with: we match it to your deposit by the last 4 digits. Never enter your full card number or CVV anywhere on this page.',
};

/** Add a bank account (wire tab) or card (card tab) in `currency`. A div, not a form: it sits inside the withdrawal form. */
export function AddPayoutAccount({ kind, currency, onAdded, onCancel }: { kind: PayoutKind; currency: PayoutCurrency; onAdded: (a: PayoutAccount) => void; onCancel: () => void }) {
  const specs = kind === 'card' ? SPECS.card : SPECS[currency];
  // Bank country starts as the country the client is connecting from (IP).
  const [v, setV] = useState<Record<string, string>>(() => (IP_COUNTRY && specs.some((s) => s.key === 'bankCountry') ? { bankCountry: IP_COUNTRY } : {}) as Record<string, string>);
  const [label, setLabel] = useState('');
  const [bad, setBad] = useState<{ field: string; error: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idp = `pa-${kind}-${currency}`;
  const ibanCountry = useMemo(() => {
    const cc = (v.iban ?? '').replace(/\s/g, '').slice(0, 2).toUpperCase();
    return IBAN_LENGTHS[cc] ? countryName(cc) : null;
  }, [v.iban]);
  const missing = specs.some((s) => !s.optional && !(v[s.key] ?? '').trim());

  const set = (k: string, val: string) => {
    setV((o) => ({ ...o, [k]: val }));
    if (bad?.field === k) setBad(null);
  };
  const save = async () => {
    setErr(null);
    const parsed = parsePayoutDetails(kind, currency, v);
    if (!parsed.ok) {
      setBad({ field: parsed.field, error: parsed.error });
      return;
    }
    setBusy(true);
    try {
      onAdded(await api.post<PayoutAccount>('/api/withdrawals/payout-accounts', { kind, currency, label, details: v }));
    } catch (x) {
      const field = x instanceof ApiError && x.code?.startsWith('invalid_') ? x.code.slice(8) : null;
      if (field && specs.some((s) => s.key === field)) setBad({ field, error: errMsg(x) });
      else setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  const title = kind === 'card' ? `Add a ${currency} card` : `Add a ${currency} bank account`;
  return (
    <div role="group" aria-label={title} className="flex flex-col gap-5 rounded-[20px] bg-track p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <b className="text-[16px]">{title}</b>
        <span className="rounded-full bg-accent-bg px-2.5 py-0.5 text-[13px] font-semibold text-accent-fg">{kind === 'card' ? 'Card refund' : PAYOUT_RAIL[currency]}</span>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {specs.map((s) => {
          const id = `${idp}-${s.key}`;
          const hint = s.key === 'iban' && ibanCountry ? `Bank country: ${ibanCountry}` : s.hint;
          return (
            <Field
              key={s.key}
              htmlFor={id}
              className={cx(s.wide && 'md:col-span-2')}
              label={
                <>
                  {s.label}
                  {s.optional && <span className="font-normal text-muted">(optional)</span>}
                </>
              }
              hint={hint}
              error={bad?.field === s.key ? bad.error : null}
            >
              {s.select ? (
                <select id={id} className="field" value={v[s.key] ?? ''} onChange={(e) => set(s.key, e.target.value)}>
                  {s.select.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={id}
                  className={cx('field', s.mono && 'num !text-[15px]')}
                  placeholder={s.placeholder}
                  inputMode={s.numeric ? 'numeric' : undefined}
                  autoComplete="off"
                  maxLength={s.key === 'last4' ? 4 : s.key === 'expiry' ? 5 : 140}
                  value={v[s.key] ?? ''}
                  onChange={(e) => set(s.key, e.target.value)}
                />
              )}
            </Field>
          );
        })}
        <Field label="Save as" htmlFor={`${idp}-label`} hint="Only you see this name" className="md:col-span-2">
          <input id={`${idp}-label`} className="field" placeholder={kind === 'card' ? `e.g. Revolut ${currency} card` : `e.g. My ${currency} account`} maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
      </div>
      <p className="m-0 text-[14px] text-muted">{NOTE[kind === 'card' ? 'card' : currency]} You can use it as soon as you save it. For your security, we email you when one is added.</p>
      {err && (
        <span className="text-[14px] text-down" role="alert">
          {err}
        </span>
      )}
      <div className="flex gap-3">
        <PillButton size="sm" busy={busy} disabled={missing} onClick={() => void save()}>
          Save {kind === 'card' ? 'card' : 'bank account'}
        </PillButton>
        <PillButton kind="secondary" size="sm" onClick={onCancel}>
          Cancel
        </PillButton>
      </div>
    </div>
  );
}
